import request from './request';
import { useMutation, useQuery } from '@tanstack/react-query';

const paymentApis = {
    notifyPaymentEmail: `/payment/notify-payment-email`,
    subscriptionPrices: `/payment/subscription/prices`,
    subscriptionCheckout: `/payment/subscription/checkout`,
    subscriptionPortal: `/payment/subscription/portal`,
    subscriptionStatus: `/payment/subscription/status`,
    subscriptionList: `/payment/subscription/list`,
    subscriptionMerchantList: (merchantId) => `/payment/subscription/merchant/${merchantId}`,
    subscriptionCharges: `/payment/subscription/charges`,
    subscriptionMerchantCharges: (merchantId) => `/payment/subscription/merchant/${merchantId}/charges`,
    subscriptionCancel: `/payment/subscription/cancel`
};

export async function notifyPaymentEmailApi({ merchantId }) {
    const response = await request.post(paymentApis.notifyPaymentEmail, { merchantId });
    return response.data;
}

export async function createMerchantSubscriptionPricesApi(body) {
    const response = await request.post(paymentApis.subscriptionPrices, body);
    return response.data;
}

export async function getMerchantSubscriptionPricesApi() {
    const response = await request.get(paymentApis.subscriptionPrices);
    return response.data;
}

export async function createMerchantSubscriptionCheckoutApi(body = {}) {
    const response = await request.post(paymentApis.subscriptionCheckout, body);
    return response.data;
}

export async function createMerchantSubscriptionPortalApi() {
    const response = await request.post(paymentApis.subscriptionPortal);
    return response.data;
}

export async function getMerchantSubscriptionStatusApi() {
    const response = await request.get(paymentApis.subscriptionStatus);
    return response.data;
}

export async function getMerchantSubscriptionListApi() {
    const response = await request.get(paymentApis.subscriptionList);
    return response.data;
}

export async function getAdminMerchantSubscriptionListApi(merchantId) {
    const response = await request.get(paymentApis.subscriptionMerchantList(merchantId));
    return response.data;
}

export async function cancelMerchantSubscriptionApi(body) {
    const response = await request.post(paymentApis.subscriptionCancel, body);
    return response.data;
}

export async function getMerchantSubscriptionChargesApi() {
    const response = await request.get(paymentApis.subscriptionCharges);
    return response.data;
}

export async function getAdminMerchantSubscriptionChargesApi(merchantId) {
    const response = await request.get(paymentApis.subscriptionMerchantCharges(merchantId));
    return response.data;
}

export function useMerchantSubscriptionPricesQuery(options) {
    return useQuery({
        queryKey: ['merchant-subscription-prices'],
        queryFn: getMerchantSubscriptionPricesApi,
        ...options
    });
}

export function useMerchantSubscriptionStatusQuery(options) {
    return useQuery({
        queryKey: ['merchant-subscription-status'],
        queryFn: getMerchantSubscriptionStatusApi,
        ...options
    });
}

export function useCreateMerchantSubscriptionCheckoutMutation(options) {
    return useMutation({
        mutationFn: createMerchantSubscriptionCheckoutApi,
        ...options
    });
}

export function useCreateMerchantSubscriptionPortalMutation(options) {
    return useMutation({
        mutationFn: createMerchantSubscriptionPortalApi,
        ...options
    });
}

export function useCreateMerchantSubscriptionPricesMutation(options) {
    return useMutation({
        mutationFn: createMerchantSubscriptionPricesApi,
        ...options
    });
}

export function useMerchantSubscriptionListQuery(options) {
    return useQuery({
        queryKey: ['merchant-subscription-list'],
        queryFn: getMerchantSubscriptionListApi,
        ...options
    });
}

export function useAdminMerchantSubscriptionListQuery({ merchantId, options }) {
    return useQuery({
        queryKey: ['merchant-subscription-list', merchantId],
        queryFn: () => getAdminMerchantSubscriptionListApi(merchantId),
        enabled: !!merchantId,
        ...options
    });
}

export function useCancelMerchantSubscriptionMutation(options) {
    return useMutation({
        mutationFn: cancelMerchantSubscriptionApi,
        ...options
    });
}

export function useMerchantSubscriptionChargesQuery(options) {
    return useQuery({
        queryKey: ['merchant-subscription-charges'],
        queryFn: getMerchantSubscriptionChargesApi,
        ...options
    });
}

export function useAdminMerchantSubscriptionChargesQuery({ merchantId, options }) {
    return useQuery({
        queryKey: ['merchant-subscription-charges', merchantId],
        queryFn: () => getAdminMerchantSubscriptionChargesApi(merchantId),
        enabled: !!merchantId,
        ...options
    });
}
